import { toast } from "react-toastify";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SettingsService } from '@/api';

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation.
 */
const onDefaultError = (err) => {
  if (err.response?.data?.message) {
    toast.error(err.response.data.message);
  }
};

/**
 * Custom hook for updating site configuration settings.
 * It utilizes the `useMutation` hook to update settings data.
 *
 * @param {function} onSuccess - Callback to execute when the mutation is successful.
 * @param {function} [onError=onDefaultError] - Callback to execute when the mutation fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useUpdateSettingData = (onSuccess, onError = onDefaultError) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => SettingsService.updateConfiguration(data),
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
      // Invalidate global settings cache to trigger refetch across all components
      queryClient.invalidateQueries(['globalSettings']);
    },
    onError, // Error callback
    retry: false,
  });
};

export { useUpdateSettingData };
